import { AlertTriangle, Trash2, CheckCircle, Flag } from 'lucide-react';
import Modal from './Modal';

type ConfirmVariant = 'danger' | 'success' | 'warning';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string | React.ReactNode;
  message?: string | React.ReactNode; // Alias for description
  itemName?: string;
  isLoading?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  variant?: ConfirmVariant; // 'danger' (red/delete), 'success' (green/approve), 'warning' (orange/flag)
}

export default function DeleteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  message,
  itemName,
  isLoading = false,
  confirmButtonText = 'Delete',
  cancelButtonText = 'Cancel',
  variant = 'danger',
}: DeleteConfirmationModalProps) {
  const content = message || description;

  const handleConfirm = () => {
    onConfirm();
  };

  // Variant-based styling
  const variantConfig = {
    danger: {
      buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
      icon: Trash2,
      loadingText: 'Deleting...',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
    },
    success: {
      buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
      icon: CheckCircle,
      loadingText: 'Approving...',
      iconBgClass: 'bg-green-100',
      iconClass: 'text-green-600',
    },
    warning: {
      buttonClass: 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500',
      icon: Flag,
      loadingText: 'Flagging...',
      iconBgClass: 'bg-orange-100',
      iconClass: 'text-orange-600',
    },
  };

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {cancelButtonText}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        disabled={isLoading}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${config.buttonClass}`}
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            {config.loadingText}
          </>
        ) : (
          <>
            <IconComponent className="w-4 h-4" />
            {confirmButtonText}
          </>
        )}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={footer}
      closeOnOverlayClick={!isLoading}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${config.iconBgClass}`}>
            <AlertTriangle className={`w-6 h-6 ${config.iconClass}`} />
          </div>
          <div className="flex-1">
            <div className="text-gray-700">{content}</div>
            {itemName && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>"{itemName}"</strong>
              </p>
            )}
          </div>
        </div>
        {!message?.toString().includes('cannot be undone') && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This action cannot be undone.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}